import { useCallback, useState } from 'react';

import {
	useDecisionMakerProfilePageData,
	useDMProfilePagePaginatedResponsesData,
	useDMProfilePagePaginatedTargetedPetitionsData,
} from 'src/app/pages/decisionMakerProfile/pageContext';
import { useProcessedImageUrl } from 'src/app/shared/hooks/image';
import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

type Result = ModelHookResult<
	{
		id: string;
		displayName: string;
		title: string | null;
		type: string;
		districtStateText: string;
		photoUrl: string;
		verified: boolean;
		responsesCount: number;
		petitionsCount: number;
		isDescriptionClampable: { desktop: boolean; mobile: boolean };
		descriptionClamped: boolean;
		descriptionText: string | null;
	},
	{ handleShowFullTextClick: () => void }
>;

// eslint-disable-next-line max-lines-per-function
export function useProfileCard(): Result {
	const MAX_DESCRIPTION_LENGTH = {
		mobile: 120,
		desktop: 290,
	};

	const {
		data: { description, descriptionAi, displayName, district, id, photo, state, title, verifiedAt, type },
	} = useDecisionMakerProfilePageData();

	const {
		data: { totalCount: responsesCount },
	} = useDMProfilePagePaginatedResponsesData();
	const {
		data: { totalCount: petitionsCount },
	} = useDMProfilePagePaginatedTargetedPetitionsData();

	const dmProfileImage = useProcessedImageUrl(photo?.userLarge);

	const descriptionText = description || descriptionAi;

	const transformLocation = () => {
		const x = [];
		if (district && /\d/.test(district)) {
			x.push(`District ${district}`);
		} else if (district) {
			x.push(district);
		}
		if (state) x.push(getUsState(state));
		return x.join(', ');
	};
	const districtStateText = transformLocation();

	const clampableMobile = Number(descriptionText?.length) > MAX_DESCRIPTION_LENGTH.mobile;
	const clampableDesktop = Number(descriptionText?.length) > MAX_DESCRIPTION_LENGTH.desktop;

	const isDescriptionClampable = {
		mobile: clampableMobile,
		desktop: clampableDesktop,
	};

	const [descriptionClamped, setDescriptionClamped] = useState(clampableMobile || clampableDesktop);

	const handleShowFullTextClick = useCallback(() => {
		setDescriptionClamped((curState) => !curState);
	}, []);

	return {
		data: {
			id,
			displayName,
			title,
			type,
			districtStateText,
			photoUrl: dmProfileImage || 'https://static.change.org/profile-img/default-user-profile.svg',
			verified: !!verifiedAt,
			descriptionText,
			descriptionClamped,
			isDescriptionClampable,
			responsesCount,
			petitionsCount,
		},
		actions: {
			handleShowFullTextClick,
		},
	};
}
